import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import WebPageLayout from '../../../components/layout/WebPageLayout';
import { useDonorManagement } from '../hooks/useDonorManagement';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectSelectedProject, selectProjects } from '../../../Module/Project/Presentation/projectSelectors';
import { selectSession } from '../../../Module/Auth/Presentation/authSelectors';
import { selectUserProfile } from '../../../Module/User/Presentation/userSelectors';
import ProjectDetailHeader from '../../../components/common/ProjectDetailHeader';
import DSLoading from '../../../components/designSystem/DSLoading';
import kakaoIcon from '../../../assets/Icon/project/kakao_icon.png';
import emailIcon from '../../../assets/Icon/project/email_icon.png';
import { useTranslation } from 'react-i18next';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { openMailClient } from '../../../Utils/mail';
import { buildSupporterMessageMailTemplate } from '../../../Utils/shareMailTemplates';
import { getProjectShareImageUrl, shareKakaoFeed } from '../../../Utils/kakaoShare';
import { formatUserFullName } from '../../../Utils/formatUserName';
import { formatDisplayCurrency } from '../../../Utils/formatCurrency';
import { searchProjects } from '../../../Module/Project/Presentation/projectActions';
import { selectProjectsStatus } from '../../../Module/Project/Presentation/projectSelectors';
import { PATHS } from '../../../routes/paths';
import { useClickOutside } from '../../../hooks/useClickOutside';

const DonorManagementWeb = () => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'complete' | 'fail'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const statuses = ['all', 'complete', 'fail'] as const;
  const selectedProject = useAppSelector(selectSelectedProject);
  const projects = useAppSelector(selectProjects);
  const projectsStatus = useAppSelector(selectProjectsStatus);
  const session = useAppSelector(selectSession);
  const userProfile = useAppSelector(selectUserProfile);

  const formatCurrency = useCallback(
    (amount: number, currencyType?: string) => {
      return formatDisplayCurrency(amount, currencyType);
    },
    [],
  );
  const statusLabel = useCallback(
    (status: typeof statuses[number]) => t(`page.donorManagement.filters.statusOptions.${status}`),
    [t],
  );

  const location = useLocation();
  const projectId = (location.state as { projectId?: string } | undefined)?.projectId;
  const userId = useMemo(
    () => (session as any)?.user?.id || (typeof window !== 'undefined' ? window.localStorage.getItem('missionfund.userId') : null) || '',
    [session],
  );

  useEffect(() => {
    if (!projectId && userId && projects.length === 0 && projectsStatus !== 'loading') {
      dispatch(searchProjects({ limit: 100, params: [{ fld: 'user_id', op: 'eq', val: userId }], sort: 'created_at:desc' }));
    }
  }, [dispatch, projectId, userId, projects.length, projectsStatus]);

  useEffect(() => {
    if (!projectId && projectsStatus === 'succeeded' && projects.length === 0) {
      navigate(PATHS.PROJECT_MANAGEMENT, { replace: true });
    }
  }, [projectId, projectsStatus, projects.length, navigate]);

  const currentProject = useMemo(() => {
    if (projectId) {
      const found = projects.find((p) => p.project_id === projectId);
      if (found) return found;
      // If not found in list but selectedProject matches, use that (fallback)
      if (selectedProject?.project_id === projectId) return selectedProject;
    }
    return selectedProject || projects[0];
  }, [projectId, selectedProject, projects]);

  const projectTitle = currentProject?.title || t('common.projectTitlePlaceholder');
  const fallbackFullName = formatUserFullName(
    (session as { user?: { first_name?: string } } | undefined)?.user?.first_name,
    (session as { user?: { last_name?: string } } | undefined)?.user?.last_name,
  );
  const userFullName =
    (session as { user?: { name?: string } } | undefined)?.user?.name ??
    (fallbackFullName ? fallbackFullName : undefined) ??
    (session as { user?: { email?: string } } | undefined)?.user?.email ??
    '';
  const senderEmail = ((session as { user?: { email?: string } } | undefined)?.user?.email ?? '').trim();
  const fundraiserName =
    userFullName || (currentProject as any)?.user_full_name || (currentProject as any)?.fundraiser_name || '';

  // 라우터로 전달된 projectId를 우선 사용해 API 호출 누락을 방지
  const effectiveProjectId = projectId ?? currentProject?.project_id;
  // API 연동 훅 사용
  const { summary, donors, hasData, isLoading, isLoadingMore, hasMore, loadMoreDonors, fetchDonorsAllPages } =
    useDonorManagement(effectiveProjectId);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // 무한 스크롤: 하단 감지 시 더 불러오기
  useEffect(() => {
    if (!hasMore || isLoadingMore || isLoading) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreDonors();
      },
      { rootMargin: '400px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, isLoading, loadMoreDonors]);

  const projectUrl = `https://go.missionfund.org/${effectiveProjectId || ''}`;

  const buildSupporterMail = useCallback(
    () =>
      buildSupporterMessageMailTemplate({
        fundraiserName,
        projectUrl,
      }),
    [fundraiserName, projectUrl],
  );

  const shareToKakao = useCallback(
    (url: string) => {
      const kakao = (window as any).Kakao;
      if (!(kakao?.Share?.sendDefault ?? kakao?.Link?.sendDefault)) {
        return;
      }
      const restrictedRegion = userProfile?.restricted_region === true || String(userProfile?.restricted_region) === 'true';
      shareKakaoFeed(kakao, {
        url,
        title: projectTitle || t('common.shareModal.defaultTitle'),
        imageUrl: getProjectShareImageUrl(currentProject as { image_url?: string; video_url?: string }),
        buttonTitle: t('common.shareModal.buttonDetail'),
        fundraiserName: fundraiserName || t('common.share.fundraiserFallback'),
        restrictedRegion,
      });
    },
    [currentProject, fundraiserName, projectTitle, t, userProfile?.restricted_region],
  );

  const runKakaoShare = useCallback(() => {
    const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
    const projectUrl = `https://go.missionfund.org/${effectiveProjectId || ''}`;

    if (!(window as any).Kakao) {
      const script = document.createElement('script');
      script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        if (!(window as any).Kakao.isInitialized()) {
          (window as any).Kakao.init(KAKAO_KEY);
        }
        shareToKakao(projectUrl);
      };
      document.head.appendChild(script);
      return;
    }

    if (!(window as any).Kakao.isInitialized()) {
      (window as any).Kakao.init(KAKAO_KEY);
    }
    shareToKakao(projectUrl);
  }, [effectiveProjectId, shareToKakao]);

  const runEmailShare = useCallback(() => {
    const { subject, body } = buildSupporterMail();
    openMailClient({ subject, body });
  }, [buildSupporterMail]);

  const handleBulkEmail = useCallback(async () => {
    const fundraiserTo = senderEmail;
    if (!fundraiserTo) {
      alert(t('page.donorManagement.alerts.bulkEmailNeedFundraiserEmail'));
      return;
    }
    const allRows = await fetchDonorsAllPages();
    const donorEmails = Array.from(
      new Set(
        allRows
          .map((donor) => donor.email?.trim())
          .filter((email): email is string => Boolean(email))
      )
    );
    if (donorEmails.length === 0) {
      alert(t('page.donorManagement.alerts.bulkEmailNoDonors'));
      return;
    }
    const cc = donorEmails.filter((e) => e.toLowerCase() !== fundraiserTo.toLowerCase());
    const { subject, body } = buildSupporterMail();
    openMailClient({
      to: fundraiserTo,
      cc: cc.length > 0 ? cc : undefined,
      subject,
      body,
    });
  }, [buildSupporterMail, fetchDonorsAllPages, senderEmail, t]);

  const handleDonorEmail = useCallback((email?: string) => {
    const to = email?.trim();
    if (!to) return;
    const { subject, body } = buildSupporterMail();
    openMailClient({ to, subject, body });
  }, [buildSupporterMail]);

  const handleOpenShareModal = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  const handleCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  const handleShareKakaoFromModal = useCallback(() => {
    setIsShareModalOpen(false);
    runKakaoShare();
  }, [runKakaoShare]);

  const handleShareEmailFromModal = useCallback(() => {
    setIsShareModalOpen(false);
    runEmailShare();
  }, [runEmailShare]);

  const handleModalInnerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);
  const closeDonorDropdowns = useCallback(() => {
    setIsStatusDropdownOpen(false);
  }, []);
  useClickOutside(closeDonorDropdowns, statusDropdownRef);

  const filterDonorBySearchAndStatus = useCallback((donor: { name: string; status: 'complete' | 'fail' | 'cancel' }) => {
    const matchesStatus = selectedStatus === 'all' || donor.status === selectedStatus;
    const trimmed = searchTerm.trim().toLocaleLowerCase();
    const matchesSearch = trimmed.length === 0 || donor.name.toLocaleLowerCase().includes(trimmed);
    return matchesStatus && matchesSearch;
  }, [searchTerm, selectedStatus]);

  const filteredDonors = useMemo(() => donors.filter(filterDonorBySearchAndStatus), [donors, filterDonorBySearchAndStatus]);

  const handleCopyLink = useCallback(async () => {
    const url = `http://go.MissionFund.org/${effectiveProjectId || ''}`;
    try {
      await navigator.clipboard.writeText(url);
      alert(t('common.messages.linkCopied'));
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(t('common.messages.linkCopied'));
    }
  }, [effectiveProjectId, t]);


  const handleExcelDownload = useCallback(async () => {
    if (!hasData) return;

    const exportRows = await fetchDonorsAllPages();
    const filteredExportRows = exportRows.filter(filterDonorBySearchAndStatus);
    if (filteredExportRows.length === 0) return;

    const headerStyle = {
      fill: { fgColor: { rgb: "D3D3D3" } },
      font: { bold: false, color: { rgb: "000000" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const cellStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const rightAlignStyle = {
      alignment: { horizontal: "right", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const commentStyle = {
      alignment: { horizontal: "left", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const headers = [
      "No", "Donor name", "Phone", "Email", "Address",
      "Donation Type", "Payment", "Status", "History",
      "Amount", "Date", "Comments"
    ];

    const formatExcelAmount = (amount: number, currencyType?: string) => (
      formatDisplayCurrency(amount, currencyType, { useSymbol: true })
    );

    const data = filteredExportRows.map((d, idx) => ([
      filteredExportRows.length - idx,
      d.name,
      d.phone,
      d.email,
      d.address || "",
      d.donationType,
      d.method.toUpperCase(),
      d.status === 'cancel' ? 'cancelled' : (d.status === 'fail' ? 'failed' : 'active'),
      "",
      formatExcelAmount(d.amount, d.currencyType),
      d.date,
      d.message
    ]));

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

    const wscols = [
      { wch: 5 },
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
      { wch: 20 },
      { wch: 15 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 15 },
      { wch: 15 },
      { wch: 80 }
    ];
    ws['!cols'] = wscols;

    if (ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: 0, c: C });
        if (ws[address]) ws[address].s = headerStyle;
      }
      for (let R = 1; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_cell({ r: R, c: C });
          if (ws[address]) {
            ws[address].s = cellStyle;
            // Amount column (index 9) right alignment
            if (C === 9) {
              ws[address].s = rightAlignStyle;
            }
            // Comments column (index 11) wrap text
            if (C === 11) {
              ws[address].s = commentStyle;
            }
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const dateCompact = `${yyyy}${mm}${dd}`;
    const projectIdForFile = String(currentProject?.project_id ?? effectiveProjectId ?? 'project')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '_');
    XLSX.utils.book_append_sheet(wb, ws, `Donations_${dateCompact}`);
    const fileName = `donations_${projectIdForFile}_${dateCompact}.xlsx`;

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
  }, [currentProject?.project_id, effectiveProjectId, fetchDonorsAllPages, filterDonorBySearchAndStatus, hasData]);

  const renderEmptyState = useCallback(() => (
    <EmptyStateWrapper>
      <EmptyTitleGroup>
        <EmptyTitleBlue>
          {t('common.empty.titleBlueLine1')} {t('common.empty.titleBlueLine2')}
        </EmptyTitleBlue>
        <EmptyTitleBlack>
          {t('common.empty.titleBlackLine1')} {t('common.empty.titleBlackLine2')}
        </EmptyTitleBlack>
      </EmptyTitleGroup>
      <ShareBtn onClick={handleOpenShareModal}>
        <ShareIcon src="/Image/posts/shareIcon.png" alt={t('common.empty.shareAlt')} />
        {t('common.empty.share')}
      </ShareBtn>

      {isShareModalOpen && (
        <ModalOverlay onClick={handleCloseShareModal}>
          <ModalContainer onClick={handleModalInnerClick}>
            <ModalHeader>
              <ModalTitleGroup>
                <ModalTitle>{t('page.donorManagement.shareModal.title')}</ModalTitle>
                <ModalDesc>{t('common.shareModal.descLine1')} {t('common.shareModal.descLine2')}</ModalDesc>
              </ModalTitleGroup>
              <CloseIcon onClick={handleCloseShareModal} />
            </ModalHeader>
            <ModalBody>
              <ShareOption onClick={handleShareKakaoFromModal}>
                <KakaoIconBox>
                  <KakaoIconImg src={kakaoIcon} alt={t('common.shareModal.kakaoAlt')} />
                </KakaoIconBox>
                <ShareOptionText>{t('common.shareModal.kakaoOption')}</ShareOptionText>
              </ShareOption>
              <ShareOption onClick={handleShareEmailFromModal}>
                <EmailIconBox>
                  <EmailIconLarge src={emailIcon} alt={t('common.shareModal.emailAlt')} />
                </EmailIconBox>
                <ShareOptionText>{t('common.shareModal.emailOption')}</ShareOptionText>
              </ShareOption>
            </ModalBody>
            <ModalFooter>
              <LinkBox>
                <LinkLabel>{t('common.shareModal.linkLabel')}</LinkLabel>
                <LinkUrl>{`https://go.missionfund.org/${effectiveProjectId || ''}`}</LinkUrl>
              </LinkBox>
              <CopyButton onClick={handleCopyLink}>
                <CopyIcon />
                {t('common.shareModal.copyLink')}
              </CopyButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </EmptyStateWrapper>
  ), [
    effectiveProjectId,
    handleCloseShareModal,
    handleCopyLink,
    handleModalInnerClick,
    handleShareEmailFromModal,
    handleShareKakaoFromModal,
    handleOpenShareModal,
    isShareModalOpen,
    t,
  ]);

  return (
    <WebPageLayout title={t('common.layout.projectManagement')} sidebarActive="donors">
      <Container>
        <ProjectDetailHeader title={projectTitle} projectId={currentProject?.project_id} variant="web" />

        <PageTitleSection>
          <PageTitle>{t('page.donorManagement.pageTitle')}</PageTitle>
        </PageTitleSection>

        {isLoading ? (
          <LoadingWrapper>
            <DSLoading variant="content" />
          </LoadingWrapper>
        ) : !hasData ? (
          renderEmptyState()
        ) : (
          <>
            <SummaryGrid>
                <SummaryCard variant="yellow">
                  <CardLabelGroup>
                    <CardTitle>{t('page.donorManagement.summary.totalTitle')}</CardTitle>
                    <CardDesc>{t('page.donorManagement.summary.totalDesc')}</CardDesc>
                  </CardLabelGroup>
                  <CardValue>{summary.total}</CardValue>
                </SummaryCard>

                <SummaryCard variant="white">
                  <CardLabelGroup>
                    <CardTitle>{t('page.donorManagement.summary.cancelledTitle')}</CardTitle>
                    <CardDesc>{t('page.donorManagement.summary.cancelledDesc')}</CardDesc>
                  </CardLabelGroup>
                  <CardValue>{summary.cancelled}</CardValue>
                </SummaryCard>

                <SummaryCard variant="white">
                  <CardLabelGroup>
                    <CardTitle>{t('page.donorManagement.summary.failedTitle')}</CardTitle>
                    <CardDesc>{t('page.donorManagement.summary.failedDesc')}</CardDesc>
                  </CardLabelGroup>
                  <CardValue>{summary.failed}</CardValue>
                </SummaryCard>

                <SummaryCard variant="blue">
                  <CardLabelGroup>
                    <CardTitle highlight={true}>{t('page.donorManagement.summary.inProgressTitle')}</CardTitle>
                    <CardDesc>{t('page.donorManagement.summary.inProgressDesc')}</CardDesc>
                  </CardLabelGroup>
                  <CardValue color="blue">{summary.inProgress}</CardValue>
                </SummaryCard>
              </SummaryGrid>

            <ActionSection>
              <ButtonGroup>
                <EmailAllButton onClick={handleBulkEmail}>
                  <EmailIcon />
                  {t('page.donorManagement.actions.emailBulk')}
                </EmailAllButton>
                <ExcelButton onClick={handleExcelDownload}>
                  <DownloadIcon />
                  {t('common.actions.downloadExcel')}
                </ExcelButton>
              </ButtonGroup>
              <HelperText>{t('page.donorManagement.helper')}</HelperText>
            </ActionSection>

            <DonorFilterSection>
              <DonorFilterGroup>
                <DonorSearchInputWrapper>
                  <DonorSearchInput
                    placeholder={t('page.paymentStatus.filters.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <DonorSearchIcon />
                </DonorSearchInputWrapper>
                <div ref={statusDropdownRef}>
                  <DonorSelectWrapper isOpen={isStatusDropdownOpen} onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}>
                    <DonorSelectedText isPlaceholder={selectedStatus === 'all'}>
                      {selectedStatus === 'all' ? t('page.donorManagement.filters.statusSelect') : statusLabel(selectedStatus)}
                    </DonorSelectedText>
                    <DonorArrowIcon isOpen={isStatusDropdownOpen} />
                    {isStatusDropdownOpen && (
                      <DonorDropdownList>
                        {statuses.map((status) => (
                          <DonorDropdownItem
                            key={status}
                            isActive={selectedStatus === status}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedStatus(status);
                              setIsStatusDropdownOpen(false);
                            }}
                          >
                            {statusLabel(status)}
                          </DonorDropdownItem>
                        ))}
                      </DonorDropdownList>
                    )}
                  </DonorSelectWrapper>
                </div>
              </DonorFilterGroup>
            </DonorFilterSection>

            <DonorList>
              {filteredDonors.map((donor, index) => (
                <DonorItem
                  key={donor.id}
                  isFirst={index === 0}
                  isLast={index === filteredDonors.length - 1}
                  isCancelled={donor.status === 'cancel'}
                >
                  <ProfileImg src={donor.profileImg} alt={donor.name} />
                  <ItemContent>
                    <ItemHeader>
                      <DonorName color={donor.status === 'cancel' ? 'gray' : 'black'}>
                        {donor.name}
                      </DonorName>
                      <BadgeGroup>
                        <Badge type={donor.method}>{t(`common.payment.method.${donor.method}`)}</Badge>
                        <Badge type={donor.status}>{statusLabel(donor.status)}</Badge>
                      </BadgeGroup>
                    </ItemHeader>

                    <DonorInfoGroup>
                      {donor.email?.trim() && (
                      <InfoItem>
                        <EmailIconSmall />
                        {donor.email}
                      </InfoItem>
                      )}
                      <InfoItem>
                        <PhoneIcon />
                        {donor.phone}
                      </InfoItem>
                      <InfoItem>
                        <DateIcon />
                        {donor.date}
                      </InfoItem>
                    </DonorInfoGroup>

                    {donor.message ? <DonorMessage>{donor.message}</DonorMessage> : null}

                    <ItemFooter>
                      <AmountSection>
                        <AmountBox>
                          <AmountLabel>{t('page.donorManagement.labels.amount')}</AmountLabel>
                          <AmountValue>{formatCurrency(donor.amount, donor.currencyType)}</AmountValue>
                        </AmountBox>
                      </AmountSection>
                      {donor.email?.trim() && (
                      <EmailButton type="button" onClick={() => handleDonorEmail(donor.email)}>
                        <EmailIconSmallBlue />
                        {t('page.donorManagement.actions.sendEmail')}
                      </EmailButton>
                      )}
                    </ItemFooter>
                  </ItemContent>
                </DonorItem>
              ))}
              {filteredDonors.length === 0 ? (
                <NoResults>
                  <NoResultsText>{t('page.paymentStatus.details.noResults')}</NoResultsText>
                </NoResults>
              ) : null}
              {hasMore && <div ref={loadMoreRef} style={{ height: 1, minHeight: 1 }} aria-hidden />}
              {isLoadingMore && (
                <LoadingMoreRow>
                  <DSLoading variant="content" />
                </LoadingMoreRow>
              )}
            </DonorList>
          </>
        )}
      </Container>
    </WebPageLayout>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding-right: 0;
  padding-bottom: 90px;
`;

const PageTitleSection = styled.div`
  margin-bottom: 12px;
`;

const PageTitle = styled.h1`
  ${({ theme }) => theme.typography.head.semiBold24}
  font-size: clamp(20px, 1.65vw, 24px);
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div<{ variant: 'yellow' | 'white' | 'blue' }>`
  height: 163px;
  padding: 16px 20px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  
  background: ${({ theme, variant }) => {
    if (variant === 'yellow') return theme.colors.yellow[5] || '#fffaea';
    if (variant === 'blue') return theme.colors.primaryBlue[5] || '#f8fdff';
    return 'white';
  }};

  ${({ theme, variant }) => variant === 'yellow' && `border: 1.5px solid ${theme.colors.yellow[50] || '#ffe390'};`}
  ${({ theme, variant }) => variant === 'blue' && `border: 1.5px solid ${theme.colors.primaryBlue[40] || '#2bc2e1'};`}
`;

const CardLabelGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitle = styled.span<{ highlight?: boolean }>`
  ${({ theme }) => theme.typography.contents.medium20 || theme.typography.head.semiBold20}
  font-size: clamp(16px, 1.4vw, 20px);
  color: ${({ theme, highlight }) => highlight ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90]};
`;

const CardDesc = styled.span`
  ${({ theme }) => theme.typography.contents.regular16}
  font-size: clamp(13px, 1.1vw, 16px);
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const CardValue = styled.span<{ color?: 'blue' }>`
  ${({ theme }) => theme.typography.contents.medium28}
  font-size: clamp(21px, 1.95vw, 28px);
  color: ${({ theme, color }) => color === 'blue' ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90]};
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const EmailAllButton = styled.button`
  width: 210px;
  height: 52px;
  background: ${({ theme }) => theme.colors.primaryBlue[5] || '#f8fdff'};
  border: 2px solid ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  ${({ theme }) => theme.typography.button.semiBold20}
  font-size: clamp(16px, 1.4vw, 20px);
  color: ${({ theme }) => theme.colors.primaryBlue[50] || '#25b9d8'};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryBlue[15] || '#def9ff'};
    color: ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  }

  &:active {
    background: ${({ theme }) => theme.colors.primaryBlue[20] || '#b7f2ff'};
    color: ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  }
`;

const ExcelButton = styled.button`
  width: 210px;
  height: 52px;
  background: ${({ theme }) => theme.colors.green[65] || '#21a366'};
  border: none;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  ${({ theme }) => theme.typography.button.semiBold20}
  font-size: clamp(16px, 1.4vw, 20px);
  color: white;
  cursor: pointer;
`;

const HelperText = styled.p`
  ${({ theme }) => theme.typography.contents.regular16}
  font-size: clamp(13px, 1.1vw, 16px);
  color: ${({ theme }) => theme.colors.grayScale[70]};
  margin: 0;
`;

const DonorFilterSection = styled.div`
  margin-bottom: 20px;
`;

const DonorFilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DonorSearchInputWrapper = styled.div`
  width: 228px;
  height: 52px;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 8px;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const DonorSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  background-color: transparent;
  min-width: 0;

  &::placeholder {
    color: #929292;
    font-family: Pretendard;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }
`;

const DonorSearchIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z' stroke='%23929292' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M21 21L16.65 16.65' stroke='%23929292' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const DonorSelectWrapper = styled.div<{ isOpen: boolean }>`
  width: 228px;
  box-sizing: border-box;
  height: 52px;
  border: 1px solid ${({ theme, isOpen }) => (isOpen ? theme.colors.primaryBlue[40] : theme.colors.grayScale[20])};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  cursor: pointer;
  position: relative;
  background: white;
`;

const DonorSelectedText = styled.span<{ isPlaceholder?: boolean }>`
  min-width: 0;
  flex: 1;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: ${({ theme, isPlaceholder }) => (isPlaceholder ? '#929292' : theme.colors.grayScale[90])};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DonorArrowIcon = styled.span<{ isOpen: boolean }>`
  width: 12px;
  height: 12px;
  border-left: 2px solid ${({ theme }) => theme.colors.grayScale[60]};
  border-bottom: 2px solid ${({ theme }) => theme.colors.grayScale[60]};
  transform: ${({ isOpen }) => (isOpen ? 'rotate(135deg)' : 'rotate(-45deg)')};
  transition: transform 0.2s;
  margin-top: ${({ isOpen }) => (isOpen ? '4px' : '-2px')};
  flex-shrink: 0;
`;

const DonorDropdownList = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  z-index: 10;
  padding: 6px;
`;

const DonorDropdownItem = styled.div<{ isActive: boolean }>`
  padding: 10px 12px;
  border-radius: 4px;
  ${({ theme }) => theme.typography.contents.regular20}
  font-size: clamp(15px, 1.4vw, 20px);
  color: ${({ theme, isActive }) => (isActive ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90])};
  background: ${({ isActive, theme }) => (isActive ? theme.colors.primaryBlue[5] : 'white')};
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryBlue[5]};
    color: ${({ theme }) => theme.colors.primaryBlue[40]};
  }
`;

const NoResults = styled.div`
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoResultsText = styled.div`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[60]};
  text-align: center;
`;

const DonorList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 12px;
  overflow: hidden;
`;

const DonorItem = styled.div<{ isFirst: boolean; isLast: boolean; isCancelled?: boolean }>`
  padding: 20px 30px;
  display: flex;
  gap: 42px;
  align-items: center;
  background: ${({ isCancelled, theme }) => isCancelled ? (theme.colors.grayScale[10] || '#f0f1f2') : 'white'};
  border-bottom: ${({ isLast, theme }) => isLast ? 'none' : `1px solid ${theme.colors.grayScale[20]}`};
`;

const ProfileImg = styled.img`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  object-fit: cover;
`;

const ItemContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const ItemHeader = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DonorName = styled.span<{ color: 'black' | 'gray' }>`
  ${({ theme }) => theme.typography.contents.medium20 || theme.typography.head.semiBold20}
  font-size: clamp(16px, 1.4vw, 20px);
  color: ${({ theme, color }) => color === 'gray' ? theme.colors.grayScale[70] : theme.colors.grayScale[90]};
`;

const BadgeGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const Badge = styled.span<{ type: 'cms' | 'card' | 'complete' | 'fail' | 'cancel' }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: clamp(10px, 0.8vw, 12px);
  ${({ theme, type }) => {
    switch (type) {
      case 'cms': return `background: ${theme.colors.primaryBlue[80] || '#026478'}; color: white;`;
      case 'card': return `background: ${theme.colors.primaryBlue[10] || '#ebfcff'}; color: ${theme.colors.primaryBlue[50] || '#25b9d8'};`;
      case 'complete': return `background: ${theme.colors.primaryBlue[20] || '#b7f2ff'}; color: ${theme.colors.grayScale[90]};`;
      case 'fail': return `background: white; border: 1px solid ${theme.colors.other?.red5 || '#ffc0c0'}; color: ${theme.colors.other?.red10 || '#ff0000'};`;
      case 'cancel': return `background: ${theme.colors.grayScale[30] || '#e5e5e5'}; color: ${theme.colors.grayScale[50] || '#bcbcbc'};`;
    }
  }}
`;

const DonorInfoGroup = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  ${({ theme }) => theme.typography.contents.regular16}
  font-size: clamp(13px, 1.1vw, 16px);
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const DonorMessage = styled.p`
  ${({ theme }) => theme.typography.contents.regular16}
  font-size: clamp(13px, 1.1vw, 16px);
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
  min-height: 48px;
  display: flex;
  align-items: center;
`;

const ItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const AmountSection = styled.div`
  display: flex;
  gap: 8px;
`;

const AmountBox = styled.div`
  width: 202px;
  padding: 10px 20px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AmountLabel = styled.span`
  ${({ theme }) => theme.typography.contents.regular16}
  font-size: clamp(13px, 1.1vw, 16px);
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const AmountValue = styled.span`
  ${({ theme }) => theme.typography.contents.medium20}
  font-size: clamp(16px, 1.4vw, 20px);
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const EmailButton = styled.button`
  width: 180px;
  height: 48px;
  background: ${({ theme }) => theme.colors.primaryBlue[5] || '#f8fdff'};
  border: 1px solid ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  ${({ theme }) => theme.typography.contents.regular16}
  font-size: clamp(13px, 1.1vw, 16px);
  color: ${({ theme }) => theme.colors.primaryBlue[50] || '#25b9d8'};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryBlue[15] || '#def9ff'};
    color: ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  }

  &:active {
    background: ${({ theme }) => theme.colors.primaryBlue[20] || '#b7f2ff'};
    color: ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  }
`;

const EmailIcon = styled.div`
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z' fill='%2325B9D8'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const EmailIconSmall = styled.div`
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.3333 2.66666H2.66666C1.93333 2.66666 1.33333 3.26666 1.33333 3.99999V12C1.33333 12.7333 1.93333 13.3333 2.66666 13.3333H13.3333C14.0667 13.3333 14.6667 12.7333 14.6667 12V3.99999C14.6667 3.26666 14.0667 2.66666 13.3333 2.66666ZM13.3333 5.33332L8 8.66666L2.66666 5.33332V3.99999L8 7.33332L13.3333 3.99999V5.33332Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const EmailIconSmallBlue = styled(EmailIconSmall)`
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.3333 2.66666H2.66666C1.93333 2.66666 1.33333 3.26666 1.33333 3.99999V12C1.33333 12.7333 1.93333 13.3333 2.66666 13.3333H13.3333C14.0667 13.3333 14.6667 12.7333 14.6667 12V3.99999C14.6667 3.26666 14.0667 2.66666 13.3333 2.66666ZM13.3333 5.33332L8 8.66666L2.66666 5.33332V3.99999L8 7.33332L13.3333 3.99999V5.33332Z' fill='%2325B9D8'/%3E%3C/svg%3E");
`;

const DownloadIcon = styled.div`
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.97917 19.8042 4.5875 19.4125C4.19583 19.0208 4 18.55 4 18V15H6V18H18V15H20V18C20 18.55 19.8042 19.0208 19.4125 19.4125C19.0208 19.8042 18.55 20 18 20H6Z' fill='white'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const PhoneIcon = styled.div`
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.68 14.91 16.08 14.82 16.43 14.94C17.55 15.31 18.76 15.51 20 15.51C20.55 15.51 21 15.96 21 16.51V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.24 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const DateIcon = styled.div`
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM19 19H5V8H19V19Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const EmptyStateWrapper = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 20px;
  padding: 28px 48px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 80px;
  width: 100%;
`;

const EmptyTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
`;

const EmptyTitleBlue = styled.p`
  ${({ theme }) => theme.typography.contents.regular24}
  font-size: clamp(18px, 1.65vw, 24px);
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  margin: 0;
  line-height: 1.5;
`;

const EmptyTitleBlack = styled.p`
  ${({ theme }) => theme.typography.contents.regular24}
  font-size: clamp(18px, 1.65vw, 24px);
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
  line-height: 1.5;
`;

const ShareBtn = styled.button`
  background: #026478;
  border: none;
  border-radius: 50px;
  padding: 14px 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  cursor: pointer;
  ${({ theme }) => theme.typography.button.bold24}
  font-size: clamp(18px, 1.65vw, 24px);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grayScale[0]};
  white-space: nowrap;
  max-width: 100%;
  box-sizing: border-box;

  &:hover {
    border-radius: 50px;
background: var(--PrimaryBlue-80, #026478);
    box-shadow: 0 0 5.8px 0 #357B89;
  }

  &:focus,
  &:active {
    border-radius: 50px;
background: var(--PrimaryBlue-90, #416777);
box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  }
`;

const ShareIcon = styled.img`
  width: 30px;
  height: 30px;
  object-fit: contain;
  aspect-ratio: 1/1;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContainer = styled.div`
  width: 644px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 32px 30px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const ModalTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalTitle = styled.h2`
  ${({ theme }) => theme.typography.head.semiBold24}
  font-family: Pretendard;
  font-size: clamp(18px, 1.65vw, 24px);
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
`;

const ModalDesc = styled.p`
  ${({ theme }) => theme.typography.contents.medium20}
  font-family: Pretendard;
  font-size: clamp(16px, 1.4vw, 20px);
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  margin: 0;
`;

const CloseIcon = styled.div`
  width: 20px;
  height: 20px;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M19.7011 1.74203C19.8925 1.55065 20 1.2911 20 1.02045C20 0.749813 19.8925 0.490257 19.7011 0.298884C19.5097 0.107512 19.2502 0 18.9795 0C18.7089 0 18.4493 0.107512 18.258 0.298884L10 8.55686L1.74203 0.298884C1.55065 0.107512 1.2911 1.97053e-08 1.02045 2.17217e-08C0.749813 2.37381e-08 0.490257 0.107512 0.298884 0.298884C0.107512 0.490257 2.37381e-08 0.749813 2.17217e-08 1.02045C1.97053e-08 1.2911 0.107512 1.55065 0.298884 1.74203L8.55686 10L0.298884 18.258C0.107512 18.4493 0 18.7089 0 18.9795C0 19.2502 0.107512 19.5097 0.298884 19.7011C0.490257 19.8925 0.749813 20 1.02045 20C1.2911 20 1.55065 19.8925 1.74203 19.7011L10 11.4431L18.258 19.7011C18.4493 19.8925 18.7089 20 18.9795 20C19.2502 20 19.5097 19.8925 19.7011 19.7011C19.8925 19.5097 20 19.2502 20 18.9795C20 18.7089 19.8925 18.4493 19.7011 18.258L11.4431 10L19.7011 1.74203Z' fill='%23929292'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const ModalBody = styled.div`
  padding: 0 32px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ShareOption = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
`;

const KakaoIconBox = styled.div`
  width: 60px;
  height: 60px;
  background: #f9e000;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KakaoIconImg = styled.img`
  width: 34px;
  height: 34px;
`;

const EmailIconBox = styled.div`
  width: 60px;
  height: 60px;
  background: #EBFCFF;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmailIconLarge = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
`;

const ShareOptionText = styled.span`
  ${({ theme }) => theme.typography.contents.medium20}
  font-size: clamp(16px, 1.4vw, 20px);
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const ModalFooter = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[6] || '#f8f8f8'};
  padding: 20px 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LinkBox = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[60]};
  border-right: none;
  border-radius: 10px 0 0 10px;
  padding: 10px 16px;
  width: 402px;
  height: 70px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const LinkLabel = styled.span`
  ${({ theme }) => theme.typography.contents.regular16}
  font-size: clamp(13px, 1.1vw, 16px);
  color: ${({ theme }) => theme.colors.grayScale[50] || '#bcbcbc'};
`;

const LinkUrl = styled.span`
  ${({ theme }) => theme.typography.contents.medium20}
  font-size: clamp(16px, 1.4vw, 20px);
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const CopyButton = styled.button`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[60]};
  border-radius: 0 10px 10px 0;
  padding: 20px 32px;
  height: 70px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  ${({ theme }) => theme.typography.button.medium20}
  font-size: clamp(16px, 1.4vw, 20px);
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const CopyIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16 4H4V16H16V4ZM16 2C17.1 2 18 2.9 18 4V16C18 17.1 17.1 18 16 18H4C2.9 18 2 17.1 2 16V4C2 2.9 2.9 2 4 2H16ZM20 8V20H8V22H20C21.1 22 22 21.1 22 20V8H20Z' fill='%2317171B'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 400px;
`;

const LoadingMoreRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  min-height: 80px;
`;

export default DonorManagementWeb;
